// Game-side half of the ambient audio bed: compute the four steering
// scalars from world state each frame and hand them to the engine module.
// The scalar math is pure and unit-tested; the audio graph is not (no
// AudioContext in vitest — steerAmbient no-ops before init).

import type { Entity } from '../components'
import type { GameWorld } from '../world'
import { biomeFactor } from '../biomes'
import { steerAmbient, type AmbientScalars } from '../../engine/ambient-audio'
import { steerMusic } from '../../engine/music'
import { T } from '../tunables'

export function computeAmbientScalars(world: GameWorld, player: Entity): AmbientScalars {
  const hp = player.health
  const hpFrac = hp ? Math.max(0, hp.current / hp.max) : 1

  // Tension: nearest predator's proximity, with hard floors while anything
  // is actively attacking. "Concerned, not scared" — the pad swells before
  // the lunge_tick, giving ears a head start on the eyes.
  let tension = 0
  const px = player.position.x
  const py = player.position.y
  const radius = T.audio.tensionRadius
  for (const p of world.with('predator', 'position')) {
    const d = Math.hypot(p.position.x - px, p.position.y - py)
    if (d < radius) tension = Math.max(tension, 1 - d / radius)
    const st = p.predator!.state
    if ((st === 'windup' || st === 'lunge') && d < radius * 1.5) {
      tension = Math.max(tension, T.audio.tensionFloorWindup)
    }
  }
  for (const b of world.with('boss')) {
    const st = b.boss!.state
    if (st !== 'dormant' && st !== 'uncurling') {
      tension = Math.max(tension, T.audio.tensionFloorBossAwake)
    }
  }

  // Water rush: proximity to the nearest 'current' hazard, 1 at its edge.
  let currentProximity = 0
  for (const h of world.with('hazard', 'position', 'renderable')) {
    if (h.hazard.kind !== 'current') continue
    const d = Math.hypot(h.position.x - px, h.position.y - py)
    const reach = h.renderable.radius + T.audio.rushRadius
    if (d < reach) {
      currentProximity = Math.max(currentProximity, 1 - Math.max(0, d - h.renderable.radius) / T.audio.rushRadius)
    }
  }

  return { biomeFactor: biomeFactor(player), hpFrac, tension, currentProximity }
}

export function ambientAudioSystem(world: GameWorld, player: Entity): void {
  const s = computeAmbientScalars(world, player)
  steerAmbient(s)
  // Zone music crossfades on the same depth scalar as the visuals; threat
  // ducks the lounge so danger reads (music-goes-quiet is the tell).
  steerMusic(s.biomeFactor, s.tension)
}
